/**
 * posTerminalsAdmin.service.ts — Admin > Payments > Forward > POS Terminals
 * (legacy Point of Sale Terminals): the account's card terminals, each with its
 * HSN and the Forward merchant it takes payment through, via /pos-terminals.
 * The charge window lists the live ones. Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminPosTerminal {
  id: number;
  title: string;
  /** The terminal's serial number. */
  hsn: string;
  merchantId: number;
  merchantTitle: string;
  active: boolean;
}

export type PosTerminalPayload = Pick<AdminPosTerminal, 'title' | 'hsn' | 'merchantId'>;

export const POS_TERMINAL_TITLE_MAX = 255;
export const POS_TERMINAL_HSN_MAX = 100;

function toTerminal(raw: Record<string, unknown>): AdminPosTerminal {
  return {
    id: Number(raw.terminalForwardID) || 0,
    title: String(raw.title ?? ''),
    hsn: String(raw.hsn ?? ''),
    merchantId: Number(raw.accountForwardID) || 0,
    merchantTitle: String(raw.merchantTitle ?? ''),
    active: raw.active === true || raw.active === 1 || raw.active === '1',
  };
}

/** Every terminal, deleted ones too. */
export function listPosTerminals(): Promise<AdminPosTerminal[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/pos-terminals')
    .then((r) => (r.data.data ?? []).map(toTerminal));
}

function body(data: PosTerminalPayload) {
  return { title: data.title.trim(), hsn: data.hsn.trim(), accountForwardID: data.merchantId };
}

export function createPosTerminal(data: PosTerminalPayload): Promise<void> {
  return apiClient.post('/pos-terminals', body(data)).then(() => undefined);
}

export function updatePosTerminal(id: number, data: PosTerminalPayload): Promise<void> {
  return apiClient.put(`/pos-terminals/${id}`, body(data)).then(() => undefined);
}

export function deletePosTerminal(id: number): Promise<void> {
  return apiClient.delete(`/pos-terminals/${id}`).then(() => undefined);
}

export function restorePosTerminal(id: number): Promise<void> {
  return apiClient.post(`/pos-terminals/${id}/restore`).then(() => undefined);
}
